<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useLabStore } from '~/stores/lab';
import { useMeStore } from '~/stores/me';
import { useReferentialStore } from '~/stores/referential';
import { useSettingsStore } from '~/stores/settings';
import { useRouter } from 'vue-router';
import { useTranslations } from '~/composables/translations';
import { requirejs } from '~/modules/require';

import BackAndNextBtn from '~/components/lab/BackAndNextBtn.vue';
import ShowroomMachines from '~/components/showroom/Machines.vue';
import ShowroomOverview from '~/components/showroom/Overview.vue';
import ShowroomMap from '~/components/showroom/Map.vue';
import ShowroomList from '~/components/showroom/List.vue';
import ShowroomServices from '~/components/showroom/Services.vue';
import ShowroomMaterials from '~/components/showroom/Materials.vue';

import vuSingleCheckbox from '@ds/vuekit/src/components/vu-single-checkbox.vue';
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import vuBtn from '@ds/vuekit/src/components/vu-btn.vue';
import vuInput from '@ds/vuekit/src/components/vu-input.vue';
import vuMessage from '@ds/vuekit/src/components/vu-message/vu-message.vue';

import { MP3DPOffer } from '../../models/labs';

// NLS
const { $i18n } = useTranslations('pages/lab/showroom');

// EMITS
const emit = defineEmits(['back', 'next']);

// STORES
const lab = useLabStore();
const me = useMeStore();
const referential = useReferentialStore();
const settings = useSettingsStore();

// PROPS
type ShowroomComponentProps = {
  isPublic?: boolean;
};

const { isPublic = false } = defineProps<ShowroomComponentProps>();

// COMPUTED
const isOperator = computed(() => me.isOperator);

// VARIABLES
const router = useRouter();
const labShowroomShop = ref();
const labShowroomCollaboration = ref();
const labShowroomGallery = ref();
const labShowroomTermsOfUse = ref();

const defaultUserRole = isOperator.value || isPublic ? 'operator' : 'seller';
const showRoomsPagesNames = [
  'LabManagementShowroom',
  'LabManagementOperatorShowroom',
  'StoreShowroom',
  'LabShowroom',
];
const isStore: boolean = router.currentRoute.value.name === 'StoreShowroom';
const currentVisibleTabId = ref('');
let currentRole = defaultUserRole;
const model = ref({ skill: '', additionalEquipment: '' });
let listener: any;
const isMpfLoading = ref(true);
const isMpfShopComponentLoading = ref(true);
const shimmerLoading = ref(true);
const isLoggedIn = ref(false);
let stickyMenu: HTMLElement;
let stickyWrappers: Element[] = [];
let stickyScrollbar: HTMLElement;
const observers: ResizeObserver[] = [];
let verticalIntersectionOffset: number;

// COMPUTED
const enableStoreURL = computed(() => lab?.services?.enableStoreURL === 'TRUE');
const displayStoreURL = computed(() => router.currentRoute.value.name === 'LabManagementShowroom');
const fullPath = computed(() => settings.currentRoute.fullPath);
const isMobileDevice = computed(() => settings.isMobileDevice);
const availablePrinter = computed(() =>
  lab.printers.filter(
    (printer: { currentState: string; offers: MP3DPOffer[] }) =>
      printer.currentState === 'ENABLED' && printer.offers.length,
  ),
);
const hasShipping = computed(
  () =>
    (
      ((
        lab.deliveries.find(
          (el: { deliveryServiceName: string }) => el.deliveryServiceName === 'Shipping',
        ) || {}
      ).offers || [])[0] || {}
    ).currentState === 'ENABLED',
);
const labServices = computed(() =>
  lab.allAdditionalServices?.filter((el: any) => el.offers.length),
);
const labCertifications = computed(() => lab.services?.certification || []);
const storeURL = computed(() => `${window?.dsBaseUrl}store/${lab.services?.storeURL}`);
const isSeller = computed(() => currentRole === 'seller');
const isEditable = computed(() => !isPublic && isSeller.value);
const skillAlreadyExists = computed(
  () =>
    lab.services?.skills &&
    lab.services.skills
      .map((s: string) => s.toLowerCase())
      .includes(model.value.skill.trim().toLowerCase()),
);
const equipmentAlreadyExists = computed(
  () =>
    lab.services?.additionalEquipments &&
    lab.services.additionalEquipments
      .map((s: string) => s.toLowerCase())
      .includes(model.value.additionalEquipment.trim().toLowerCase()),
);

// METHODS
const fetchAndBuildMPFComponent = async () => {
  const labId = String(
    router.currentRoute.value.params?.id || router.currentRoute.value.params?.intlabid,
  );

  try {
    await lab.fetchLab({ id: labId, uc2: !isPublic });

    if ((isPublic && lab?.lab?.currentState === 'DRAFT') || (isStore && !enableStoreURL.value)) {
      return await notFoundRedirect();
    }

    await createMPFComponents();
  } catch (e) {
    console.error(e);
    notFoundRedirect();
  }
};

const copyStoreURL = () => {
  const el = document.createElement('textarea');

  el.value = storeURL.value;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

const toggleStoreURL = () => {
  lab.updateShopServices({
    enableStoreURL: enableStoreURL.value ? 'FALSE' : 'TRUE',
  });
};

const addSkill = () => {
  if (!model.value.skill.trim()) {
    return;
  }

  lab.updateShopServices({
    skills: [model.value.skill].concat(lab.services?.skills || []),
  });

  model.value.skill = '';
};

const addEquipment = () => {
  if (!model.value.additionalEquipment.trim()) {
    return;
  }

  lab.updateShopServices({
    additionalEquipments: [model.value.additionalEquipment].concat(
      lab.services?.additionalEquipments || [],
    ),
  });

  model.value.additionalEquipment = '';
};

const removeSkill = (el: any) => {
  const skills = lab.services?.skills.slice();
  skills?.splice(skills.indexOf(el), 1);
  lab.updateShopServices({ skills });
};

const removeEquipment = (el: any) => {
  const equipment = lab.services?.additionalEquipments.slice();
  equipment?.splice(equipment.indexOf(el), 1);
  lab.updateShopServices({ additionalEquipments: equipment });
};

const loadMaterialsOfPrinters = async () => {
  // GET ALL MATERIALS OF THE LAB BY PRINTERS AND FETCH THEM
  const materials = availablePrinter.value
    .reduce((acc: any, printer: { offers: any[] }) => {
      acc.push(...printer.offers.map((o: any) => o.materialId));
      return acc;
    }, [])
    .filter((el: any, index: number, arr: string | any[]) => index === arr.indexOf(el));
  // FETCH THEM
  await referential.fetchMaterialsByIds({ materialIds: materials, add: true });
};

const createMPFComponents = async () => {
  // TODO USE MP3DPFactoriesHelpers
  const [
    MarketplaceConnector,
    MPFFactoriesV2,
    ShopFactory,
    ShopServiceFactory,
    AddressFactory,
    ShowroomShop,
    ShowroomCollaborationGallery,
    ShowroomGallery,
    SellerSalesConditionsShowroom,
  ] = await requirejs<[any, any, any, any, any, any, any, any, any]>([
    'DS/MPFDataProxy/MarketplaceConnector',
    'DS/MPFModelFactory/MPFFactoriesV2',
    'DS/MPFShopModel/ShopFactory',
    'DS/MPFShopModel/ShopServiceFactory',
    'DS/MPFAddressModel/AddressFactory',
    'DS/MPFShowroomComponent/ShowroomShop',
    'DS/MPFShowroomComponent/ShowroomCollaborationGallery',
    'DS/MPFShowroomComponent/ShowroomGallery',
    'DS/MPFTcContractComponent/SellerSalesConditionsShowroom',
  ]);

  let shop: any;
  let shopService: any;
  let logo;
  let background;
  let address: any;
  let countries: any;
  let showcaseItems: any;
  const shopId = lab?.lab?.id; // Replace by your shop id
  let country;

  isMpfShopComponentLoading.value = true;
  listener.stopListening();

  labShowroomShop.value.innerHTML = '';
  labShowroomCollaboration.value.innerHTML = '';
  labShowroomGallery.value.innerHTML = '';

  if (
    isLoggedIn.value &&
    lab.services &&
    lab.services['contract-documents'] &&
    lab.services['contract-documents'].length
  ) {
    labShowroomTermsOfUse.value.innerHTML = '';
  }

  isMpfLoading.value = true;

  return MarketplaceConnector.fetchPromise()
    .then((connector: any) => {
      // Get instance of MPFFactories to get instances of the five needed factories
      const factories = MPFFactoriesV2.getInstance(connector);

      // Load required factories with potential constraints
      return Promise.all([
        factories.getFactory(MPFFactoriesV2.Types.SHOP),
        factories.getFactory(MPFFactoriesV2.Types.SHOP_SERVICE),
        factories.getFactory(MPFFactoriesV2.Types.LOGO),
        factories.getFactory(MPFFactoriesV2.Types.BACKGROUND),
        !isLoggedIn.value
          ? Promise.resolve(null)
          : factories.getFactory(MPFFactoriesV2.Types.ADDRESS),
        factories.getFactory(MPFFactoriesV2.Types.COUNTRY),
        factories.getFactory(MPFFactoriesV2.Types.SHOWCASE_ITEM),
        factories.getFactory(MPFFactoriesV2.Types.MEDIA),
        isLoggedIn.value &&
        lab.services &&
        lab.services['contract-documents'] &&
        lab.services['contract-documents'].length
          ? factories.getFactory(MPFFactoriesV2.Types.TC_DOCUMENT)
          : null,
      ]);
    })
    .then((factories: any) => {
      const [
        shopFactory,
        shopServiceFactory,
        logoFactory,
        backgroundFactory,
        addressFactory,
        countryFactory,
        showcaseItemFactory,
        mediaFactory,
        tcDocumentFactory,
      ] = factories;

      // Create shop, shopService and country collection
      shop = shopFactory.createModel(ShopFactory.Types.SHOP);
      shopService = shopServiceFactory.createModel(ShopServiceFactory.Types.SHOP_SHOP_SERVICE);
      shop.set(shop.parse(JSON.parse(JSON.stringify(lab.lab))));
      shopService.set(shopService.parse(JSON.parse(JSON.stringify(lab?.lab?.shopServices))));
      shopService.setParentResourceId(shopId);
      countries = countryFactory.createCollection('seller');
      showcaseItems = showcaseItemFactory.createCollection(
        isPublic ? 'publicShopServiceShowcaseItem' : 'shopServiceShowcaseItem',
        [],
        {
          parentResourceId: lab.services?.id,
        },
      );

      if (isLoggedIn.value) {
        // Create an address model and fetch the data from the back-end
        address = addressFactory.createModel(
          AddressFactory.Types.address,
          {
            id: shop.get('address'),
          },
          {
            parentResourceId: lab.services?.id,
          },
        );
      } else {
        countries = countryFactory.createCollection('seller');
      }

      // Fetch countries and then render shop component
      Promise.all([
        countries.fetchPromise(),
        !isLoggedIn.value ? Promise.resolve() : address.fetchPromise(),
      ]).then(async () => {
        // Create logo and background model
        logo = logoFactory.createModel();
        background = backgroundFactory.createModel();

        // Get the corresponing country model from the country collection
        country = !isLoggedIn.value
          ? countries.getCountryWithIso3(shop.get('country'))
          : countries.getCountryWithIso3(address.getCountry());

        // Create ShowroomShop component and inject into webpage
        new ShowroomShop({
          shop,
          shopService,
          logo,
          background,
          address,
          country,
          isSmallView: true,
          editable: isEditable.value,
          serviceTerm: 'shop',
        })
          .render()
          .inject(labShowroomShop.value);

        const [PlatformAPI] = await requirejs<[any]>(['DS/PlatformAPI/PlatformAPI']);

        listener.listenTo(shop, 'onChange:shopName', () => {
          PlatformAPI.publish('shopNameChange', {
            name: shop.getName(),
          });
        });

        listener.listenTo(
          shopService,
          'onChange:description',

          () => lab.updateShopServices({ description: shopService.getDescription() }),
        );

        isMpfShopComponentLoading.value = false;
      });

      showcaseItems.fetchPromise().then(() => {
        new ShowroomGallery({
          mediaFactory,
          showcaseItemFactory,
          showcaseItems,
          editable: isEditable.value,
        })
          .render()
          .inject(labShowroomGallery.value);

        new ShowroomCollaborationGallery({
          showcaseItemFactory,
          mediaFactory,
          showcaseItems,
          editable: isEditable.value,
        })
          .render()
          .inject(labShowroomCollaboration.value);

        if (isLoggedIn.value && lab.services && lab.services['contract-documents'].length) {
          new SellerSalesConditionsShowroom({
            model: shop,
            tcDocumentFactory,
          })
            .render()
            .inject(labShowroomTermsOfUse.value);
        }

        isMpfLoading.value = false;
      });
    });
};

const toggleToSeller = () => {
  if (currentRole === 'operator') {
    currentRole = 'seller';
  } else {
    currentRole = 'operator';
  }

  createMPFComponents();
};

const notFoundRedirect = async () => {
  const [MP3DPAlertManager] = await requirejs<[any]>(['DS/MP3DPServices/MP3DPAlertManager']);

  MP3DPAlertManager.show($i18n('labNotFound'), 'error');
  router.push({ name: 'LabsExploration' });
};

// ///////////////// Sticky menu ////////////////////////////
/** Vertical scroll */
const scrollToMenuWrapper = (id: string, offsetY = 0) => {
  const elem = document.getElementById(id);
  // We use a different scrolling because we are not using the same scrollbars
  if (isEditable.value) {
    const tabBar: HTMLElement = document.querySelector('.tab-bar-menu')!;
    const isHeaderOnTopOfStickyMenu = tabBar.offsetWidth > 250;
    stickyScrollbar.scrollTo({
      top:
        (elem ? elem.offsetTop : 0) -
        stickyMenu.offsetHeight -
        150 - // ADD this ? document.querySelector('.lab-management-header').offsetHeight
        (isHeaderOnTopOfStickyMenu ? tabBar.offsetHeight + offsetY : offsetY + 20),
      behavior: 'smooth',
    });
  } else {
    const buyerHeader: HTMLElement = document.querySelector('.showroom-buyer-header')!;
    stickyScrollbar.scrollTo({
      top: (elem ? elem.offsetTop : 0) - stickyMenu.offsetHeight - buyerHeader.offsetHeight - 90,
      behavior: 'smooth',
    });
  }
};

const scrollToStickyMenuTabHorizontally = (id: string) => {
  if (!stickyMenu) {
    return;
  }

  const elem: HTMLElement | null = document.getElementById(`menu-${id}`.replace('-wrapper', ''));
  const x = Number(elem?.offsetLeft);

  stickyMenu.scrollTo({
    left: x - 10, // Remove 10px to scroll completely to the left if needed
    behavior: 'smooth',
  });
};

const areIntersecting = (div1: Element, div2: Element, offsetY: number) => {
  const rect1 = div1.getBoundingClientRect();
  const rect2 = div2.getBoundingClientRect();
  return (
    rect1.right >= rect2.left &&
    rect1.left <= rect2.right &&
    rect1.bottom >= rect2.top &&
    rect1.top <= rect2.bottom + offsetY
  );
};

/** Refresh the menu display by auto-scrolling horizontally and underlining the active tab */
const refreshSelectedMenuTab = () => {
  const currentVisibleSection = stickyWrappers.findLast((elem: Element) =>
    areIntersecting(elem, stickyMenu, verticalIntersectionOffset),
  );
  if (currentVisibleSection && currentVisibleSection.id !== currentVisibleTabId.value) {
    currentVisibleTabId.value = currentVisibleSection.id;
    scrollToStickyMenuTabHorizontally(currentVisibleTabId.value);
  }
};

const setupStickyMenu = (options: {
  stickyMenuSelector: string;
  wrappersIds: any;
  scrollbarSelectors: any;
}) => {
  verticalIntersectionOffset = 20;
  stickyMenu = document.querySelector(options.stickyMenuSelector)!;
  stickyWrappers = options.wrappersIds.map((id: string) => document.getElementById(id));
  stickyWrappers.forEach((e: any) => {
    const observer = new ResizeObserver(refreshSelectedMenuTab);
    observer.observe(e);
    observers.push(observer);
  });
  stickyScrollbar = document.querySelector(
    options.scrollbarSelectors.find((selector: string) => document.querySelector(selector)),
  );
  stickyScrollbar.addEventListener('scroll', refreshSelectedMenuTab);
};

const destroyStickyMenu = () => {
  window.removeEventListener('wheel', refreshSelectedMenuTab);
  if (stickyMenu) {
    stickyScrollbar.removeEventListener('scroll', refreshSelectedMenuTab);
  }

  observers.forEach((o: ResizeObserver) => o.disconnect());
};

const loadShowroomNeededData = async () => {
  const labId = String(
    router.currentRoute.value.params?.id || router.currentRoute.value.params?.intlabid,
  );

  shimmerLoading.value = true;

  await lab.fetchLab({ id: labId, uc2: !isPublic });
  await loadMaterialsOfPrinters();
  await fetchAndBuildMPFComponent();

  shimmerLoading.value = false;
};

// WATCHERS
watch(
  () => fullPath,
  () => {
    if (
      lab?.lab?.id !== router.currentRoute.value.params?.id &&
      showRoomsPagesNames.includes(String(router.currentRoute.value.params?.currentRoute))
    ) {
      fetchAndBuildMPFComponent();
    }
  },
);

// BEFORE UNMOUNT
onBeforeUnmount(async () => {
  listener.stopListening();
  destroyStickyMenu();
});

// MOUNTED
onMounted(async () => {
  const [Listener] = await requirejs<[any]>(['UWA/Class/Listener']);

  listener = new Listener();

  window.dispatchEvent(new Event('resize'));

  await referential.fetchProcessFamilies();
  await referential.fetchMaterialClasses();
  await referential.fetchMachines();

  await me.fetchMe();
  isLoggedIn.value = me.isLoggedIn;

  await loadShowroomNeededData();

  setupStickyMenu({
    stickyMenuSelector: '.sticky-container',
    wrappersIds: [
      'gallery-wrapper',
      'materials-wrapper',
      'machines-and-capabilities-wrapper',
      'locations-and-details-wrapper',
    ],
    scrollbarSelectors: [
      '.custom-scroll-bar-right.custom-scroll-bar-right',
      '.custom-scroll-bar-right',
    ],
  });
});
</script>

<template>
  <div class="lab-management-showroom-container">
    <div class="mb-20px">
      <div
        v-mask="!lab.lab?.id"
        :class="['lab-showroom', { 'mp-fill-height': !isEditable }, { 'is-editable': isEditable }]"
      >
        <div
          class="custom-scroll-bar-right"
          style="height: 100%; overflow-y: auto; overflow-x: hidden"
        >
          <div class="mp-container readonly-showroom">
            <div v-if="!isSeller" class="showroom-buyer-header flex flex-row">
              <div class="showroom-section-title lab-name flex-grow-1 mt-20px">
                {{ lab.lab?.shopName }}
              </div>
            </div>
            <div v-if="isOperator" class="text-right mpf-kyc-operator-edit-button">
              <vu-single-checkbox
                switch
                :label="$i18n('editAsSeller')"
                v-model="isSeller"
                @update:model-value="toggleToSeller"
              />
            </div>
            <div v-if="displayStoreURL" id="lab-showroom-store-url">
              <div class="row">
                <div class="col-xs-12">
                  <div class="seller-store-ctn no-padding mt-20px col-md-12">
                    <div class="seller-store">
                      <div class="seller-store-title">
                        {{ $i18n('storeURL') }}
                      </div>
                      <div class="seller-store-description">
                        {{ $i18n('storeDescription') }}
                      </div>
                      <div class="row seller-store-actions flex align-items-center">
                        <div class="store-toggle-ctn col-sm-4">
                          <vu-single-checkbox
                            switch
                            :label="$i18n('enableStore')"
                            v-model="enableStoreURL"
                            @update:model-value="toggleStoreURL"
                          />
                        </div>
                        <div v-if="enableStoreURL" class="store-url-input col-sm-8">
                          <vu-input disabled v-model="storeURL" />
                          <vu-btn @click="copyStoreURL">
                            {{ $i18n('copy') }}
                          </vu-btn>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="shop-overview-container grid grid-1-columns--sm grid-2-columns grid-gap-10px my-10px"
            >
              <div class="showroom-container">
                <template v-if="isMpfShopComponentLoading">
                  <div
                    class="loading-gray-box shimmer mb-20px"
                    style="height: 435px; width: 100%"
                  />
                </template>
                <div id="lab-showroom-shop">
                  <div ref="labShowroomShop" />
                </div>
              </div>
              <!-- Header Overview -->
              <ShowroomOverview
                id="lab-showroom-overview"
                :certifications="labCertifications"
                :materials="lab.allMaterialsFromPrinters"
              />
            </div>
            <div :class="['sticky-container flex', { 'move-down': !isSeller }]">
              <!--  Case studies & clients Tab -->
              <div
                :class="[
                  'tab-bar-menu menu-tab',
                  { active: currentVisibleTabId === 'gallery-wrapper' },
                ]"
                @click="scrollToMenuWrapper('gallery-wrapper')"
              >
                <div id="menu-gallery" class="menu-tab-inner flex">
                  <vu-icon icon="picture" />
                  <div class="white-space-nowrap">
                    {{ $i18n('caseStudiesAndClients') }}
                  </div>
                </div>
              </div>
              <!-- Materials Tab -->
              <div
                :class="['menu-tab', { active: currentVisibleTabId === 'materials-wrapper' }]"
                @click="scrollToMenuWrapper('materials-wrapper')"
              >
                <div id="menu-materials" class="menu-tab-inner flex">
                  <vu-icon icon="material" />
                  <div class="white-space-nowrap">
                    {{ $i18n('materials') }}
                  </div>
                </div>
              </div>
              <!-- Machines and capabilities Tab -->
              <div
                :class="[
                  'menu-tab',
                  { active: currentVisibleTabId === 'machines-and-capabilities-wrapper' },
                ]"
                @click="scrollToMenuWrapper('machines-and-capabilities-wrapper')"
              >
                <div id="menu-machines-and-capabilities" class="menu-tab-inner flex">
                  <vu-icon icon="tools" />
                  <div class="white-space-nowrap">
                    {{ $i18n('machinesAndCapabilities') }}
                  </div>
                </div>
              </div>
              <!-- Location and details Tab -->
              <div
                :class="[
                  'menu-tab',
                  { active: currentVisibleTabId === 'locations-and-details-wrapper' },
                ]"
                @click="scrollToMenuWrapper('locations-and-details-wrapper')"
              >
                <div id="menu-locations-and-details" class="menu-tab-inner flex">
                  <vu-icon icon="location" />
                  <div class="white-space-nowrap">
                    {{ $i18n('location') }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Under Sticky Menu -->
            <div class="showroom-content flex flex-column grid-gap-10px">
              <!-- Gallery -->
              <template v-if="isMpfLoading">
                <div class="loading-gray-box shimmer mb-20px" style="height: 350px; width: 100%" />
              </template>
              <div id="gallery-wrapper">
                <div id="lab-showroom-gallery">
                  <div ref="labShowroomGallery" />
                </div>
              </div>

              <!-- Materials -->
              <div id="materials-wrapper" class="flex flex-column grid-gap-10px">
                <div class="showroom-section-title text--uppercase">
                  {{ $i18n('materials') }}
                </div>
                <template v-if="shimmerLoading">
                  <div
                    class="loading-gray-box shimmer mb-20px"
                    style="height: 200px; width: 100%"
                  />
                </template>
                <ShowroomMaterials
                  v-else
                  id="lab-showroom-materials"
                  :materials="lab.materialsByMaterialFamilyByMaterialClass"
                  :is-card-view="isMobileDevice"
                />
              </div>
              <!-- Machines & Capabilities -->
              <div id="machines-and-capabilities-wrapper">
                <div
                  id="machines-and-capabilities-title"
                  class="showroom-section-title text--uppercase"
                >
                  {{ $i18n('machinesAndCapabilities') }}
                </div>
                <template v-if="shimmerLoading">
                  <div
                    class="loading-gray-box shimmer mb-20px"
                    style="height: 200px; width: 100%"
                  />
                </template>
                <ShowroomMachines
                  v-else
                  id="lab-showroom-machines"
                  :printers="availablePrinter"
                  :is-card-view="isMobileDevice"
                  @toggle="loadMaterialsOfPrinters"
                />
                <ShowroomList
                  v-if="isPublic"
                  id="lab-showroom-skills"
                  :title="$i18n('skills')"
                  :items="lab.services?.skills"
                  :no-items-text="$i18n('noSkills')"
                />
                <ShowroomServices
                  id="lab-showroom-services"
                  :has-shipping="hasShipping"
                  :services="labServices"
                />
                <ShowroomList
                  v-if="isPublic"
                  id="lab-showroom-additional-equipment"
                  :title="$i18n('additionalEquipment')"
                  :items="lab.services?.additionalEquipments"
                  :no-items-text="$i18n('noEquipment')"
                />
                <ShowroomList
                  id="lab-showroom-certifications"
                  :items="labCertifications"
                  :title="$i18n('certifications')"
                  :no-items-text="$i18n('noCertifications')"
                />
                <div v-if="!isPublic" id="lab-showroom-skills" class="showroom-view skills">
                  <div class="skills">
                    <div class="skills-equipment-container">
                      <div class="bold-text">
                        {{ $i18n('skills') }}
                      </div>
                      <div class="grid skills-row grid-1-columns--xs">
                        <ul class="skills-list">
                          <li
                            v-for="(skill, index) in lab.services?.skills"
                            :key="'skill-' + index"
                            class="skill-list-element"
                          >
                            <span class="text">{{ skill }}</span>
                            <span
                              v-if="isSeller"
                              class="fonticon fonticon-cancel-squared element-delete"
                              @click="removeSkill(skill)"
                            />
                          </li>
                        </ul>
                        <template v-if="isSeller">
                          <div class="input-container">
                            <vu-input
                              v-model="model.skill"
                              :placeholder="$i18n('listYourSkills')"
                            />
                          </div>
                          <div class="add-skill-btn">
                            <vu-btn
                              :disabled="skillAlreadyExists"
                              style="width: 100%; margin-top: 5px"
                              @click="addSkill"
                            >
                              {{ $i18n('addSkill') }}
                            </vu-btn>
                          </div>
                        </template>
                      </div>
                      <Transition name="slide">
                        <div v-if="skillAlreadyExists">
                          <vu-message
                            :show="true"
                            color="warning"
                            :timeout="0"
                            :closable="false"
                            animate="false"
                          >
                            {{ $i18n('skillAlreadyExists') }}
                          </vu-message>
                        </div>
                      </Transition>
                      <div class="bold-text equipment-ctn">
                        {{ $i18n('additionalEquipment') }}
                      </div>
                      <div class="grid equipments-row">
                        <ul class="equipment-list">
                          <li
                            v-for="(additionalEquipment, index) in lab.services
                              ?.additionalEquipments"
                            :key="'additionalEquipment-' + index"
                            class="equipment-list-element"
                          >
                            <span class="text">{{ additionalEquipment }}</span>
                            <span
                              v-if="isSeller"
                              class="fonticon fonticon-cancel-squared element-delete"
                              @click="removeEquipment(additionalEquipment)"
                            />
                          </li>
                        </ul>
                        <template v-if="isSeller">
                          <div class="input-container">
                            <vu-input
                              v-model="model.additionalEquipment"
                              :placeholder="$i18n('listYourTools')"
                            />
                          </div>
                          <div class="add-equipment-btn">
                            <vu-btn
                              :disabled="equipmentAlreadyExists"
                              style="width: 100%; margin-top: 5px"
                              @click="addEquipment"
                            >
                              {{ $i18n('addEquipment') }}
                            </vu-btn>
                          </div>
                        </template>
                      </div>
                      <Transition name="slide">
                        <div v-if="equipmentAlreadyExists">
                          <vu-message
                            :show="true"
                            color="warning"
                            :timeout="0"
                            :closable="false"
                            animate="false"
                          >
                            {{ $i18n('equipmentAlreadyExists') }}
                          </vu-message>
                        </div>
                      </Transition>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location -->
              <div id="locations-and-details-wrapper">
                <div
                  id="locations-and-details-title"
                  class="showroom-section-title text--uppercase"
                >
                  {{ $i18n('location') }}
                </div>
                <ShowroomMap v-if="!isMpfLoading" :lab="lab.lab" />
                <template v-if="isMpfLoading">
                  <div
                    class="loading-gray-box shimmer mb-20px"
                    style="height: 200px; width: 100%"
                  />
                </template>
                <div id="lab-showroom-collaboration">
                  <div ref="labShowroomCollaboration" />
                </div>
                <div
                  v-if="
                    lab.services &&
                    lab.services['contract-documents'] &&
                    lab.services['contract-documents'].length
                  "
                  id="lab-showroom-terms-of-use"
                  ref="labShowroomTermsOfUse"
                  style="min-height: 30px"
                />
                <slot name="bottom" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <BackAndNextBtn v-if="!isOperator && isEditable" @back="emit('back')" @next="emit('next')" />
  </div>
</template>

<style lang="scss">
@forward '/assets/scss/skeletons';
@use '/assets/scss/variables' as variables;

.showroom-container {
  background: white;
  border: var(--grey-4) solid 1px;
  padding: 0px;
  border-radius: 4px;
}

.showroom-section-subtitle {
  font-family: 'arial';
  color: var(--grey-7);
  font-size: 18px;
  font-weight: bold;

  @include variables.for-sm {
    font-size: 16px;
  }
}

.tooltip-body {
  min-width: 200px;
}

.mpf-showroom-shop .mpf-showroom-shop-title {
  @include variables.for-xs {
    font-size: 18px !important;
  }
}

#lab-showroom-gallery {
  /* TODO ASK MPF TO DO IT (and the component in Vue) */
  .mpf-title {
    text-transform: uppercase;
    margin-top: 10px;
  }
}

// TODO find a way to not reproduce this
.mp3dp-link {
  font-family: Arial;
  font-size: 14px;
  color: #368ec4;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: #368ec4;

    a {
      color: #368ec4;
    }
  }
}

.expand-bloc .vu-icon {
  font-size: 14px;
}
</style>

<style scoped lang="scss">
@forward '/assets/scss/skeletons';
@use '/assets/scss/variables' as variables;

.showroom-container {
  padding: 10px;
}

.lab-showroom {
  .seller-store-actions {
    .store-toggle-ctn {
      .toggle {
        min-height: unset;
        margin: 10px 0;
      }
    }

    .store-url-input {
      display: flex;
      align-items: center;

      .form-group {
        margin: 0;
        width: 100%;
        margin-right: 10px;
      }
    }
  }

  .seller-store {
    border: 1px solid var(--grey-4);
    border-radius: 4px;
    background-color: var(--grey-0);
    padding: 15px;
    margin-bottom: 15px;

    .seller-store-title {
      font-weight: bold;
    }

    .seller-store-description {
      padding: 10px 0 5px;
    }
  }
}

.lab-management-showroom-container .is-editable:deep(.custom-scroll-bar-right) {
  max-height: calc(100vh - 355px);
}

.lab-showroom {
  overflow-x: hidden;

  &.mp-fill-height:not(.editable) {
    height: calc(100vh - 130px);

    @include variables.for-md {
      height: calc(100vh - 189px);
    }
  }

  .custom-scroll-bar-right {
    scrollbar-width: thin;
  }

  .skills-row,
  .equipments-row {
    grid-template-columns: 3fr 1fr;
    column-gap: 10px;

    .input-container {
      margin-top: 3px;
      grid-row: 2;
    }

    .add-equipment-btn,
    .add-skill-btn {
      grid-row: 2;
    }

    @include variables.for-xs {
      grid-template-columns: 1fr;

      .add-equipment-btn,
      .add-skill-btn {
        grid-row: 3;
      }
    }
  }

  &.is-editable {
    max-height: calc(100vh - 355px);

    @include variables.for-sm {
      max-height: calc(100vh - 262px);
    }

    /* Tablet horizontal handling */
    @media (max-height: 766px) {
      max-height: calc(100vh - 262px);
    }
  }

  .showroom-buyer-header {
    position: -webkit-sticky;
    position: sticky;
    top: -1px;
    gap: 20px;
    border-bottom: 1px solid var(--grey-4);
    background-color: rgb(249, 249, 249);
    overflow-x: auto;
    z-index: 25;

    .lab-name {
      text-transform: uppercase;
      max-height: 43px;
    }

    .lab-date {
      font-weight: bold;
    }
  }

  .sticky-container::-webkit-scrollbar {
    display: none;
  }

  .showroom-section-title {
    font-family: '3dslight';
    color: var(--grey-7);
    font-size: 30px;

    @include variables.for-sm {
      font-size: 22px;
    }
  }

  .sticky-container {
    background-color: rgb(249, 249, 249);
    margin: 0px auto;
    position: -webkit-sticky;
    position: sticky;
    top: 0px;
    gap: 20px;
    border-bottom: 1px solid var(--grey-4);
    overflow-x: auto;
    z-index: 10;
    scrollbar-width: none;

    /* We can push the sticky menu down to have some place to put the Readonly Showroom Header next to it */
    &.move-down {
      top: 63px;

      @include variables.for-md {
        top: 50px;
      }
    }

    .menu-tab {
      padding: 10px 10px 5px 10px;
      cursor: pointer;

      .vu-icon.fonticon {
        margin: 0;
        color: var(--grey-6);
      }

      &:hover {
        color: var(--grey-7);

        .vu-icon.fonticon {
          color: var(--grey-7);
        }
      }

      &.active {
        .vu-icon.fonticon {
          color: var(--blue-3);
        }

        border-bottom: solid 3px var(--blue-3);
        bottom: 0px;

        .menu-tab-inner {
          color: var(--grey-7);
        }
      }

      .menu-tab-inner {
        color: var(--grey-6);

        &:hover {
          color: var(--grey-7);
        }

        font-size: 16px;
        gap: 7px;
      }
    }
  }

  .element-delete {
    float: right;
  }

  .mpf-seller-sales-conditions-showroom {
    padding-left: 0px;

    h3 {
      margin-left: 0px;
    }
  }

  .mpf-showroom-gallery
    .mpf-sr-gallery-item-container
    .mpf-showroom-gallery-item
    .mpf-gallery-item-content
    .mpf-gallery-item-image {
    background-size: auto 100%;
    background-repeat: no-repeat;
  }

  /* Widget detail */
  .rowShowcase .widgetItem {
    margin-bottom: 10px;
    min-height: 300px;

    @include variables.for-xs {
      &:nth-child(2n + 1) {
        padding-right: 7px;
      }

      &:nth-child(2n) {
        padding-left: 7px;
      }
    }
  }

  .process-title,
  .material-class-name {
    font-size: 16px;
    font-weight: bold;
    color: var(--grey-6);
  }

  .skills {
    .skills-section-title {
      font-family: '3dslight';
      color: var(--grey-7);
      font-size: 30px;
    }

    .skills-equipment-container {
      .bold-text {
        font-weight: bold;
      }

      .equipment-ctn {
        margin-top: 15px;
      }
    }

    .lock {
      opacity: 0.8;
      pointer-events: none;
    }

    .list-padding {
      padding-left: 20px;
    }
  }

  .materials {
    .material-and-finishes {
      .material-and-finishes-section-title {
        font-family: '3dslight';
        color: var(--grey-7);
        font-size: 30px;
        margin-bottom: 0;
        margin-top: 0;
      }

      .collapsed + .material-content {
        display: none;
      }
    }

    .process {
      padding: 0;

      .info {
        color: var(--grey-5);
      }

      .material {
        border: 1px solid var(--grey-4);
        border-radius: 4px;
        padding: 0;
        margin: 10px 0px;
        background-color: var(--grey-0);

        .material-title {
          font-weight: bold;
          padding: 5px;
          border-bottom: 1px solid var(--grey-3);
          cursor: pointer;

          .fonticon-down-dir {
            color: var(--grey-6);
          }

          .fonticon-right-dir {
            color: var(--grey-5);
          }

          &:hover {
            .fonticon {
              color: var(--grey-7);
            }
          }
        }
      }
    }
  }

  .certifications,
  .skills {
    ul {
      font-weight: bold;
      margin-top: 12px;

      li {
        color: var(--blue-3);
        font-size: 22px;
        padding-left: 0;

        .text {
          font-weight: bold;
          color: var(--grey-7);
          font-size: 14px;
        }

        .fonticon {
          color: var(--grey-7);
          cursor: pointer;
          font-size: 14px;
        }
      }
    }
  }

  .partners-ctn {
    margin-bottom: 25px;
  }
}
</style>
